import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateIcon, ImageUploadIcon } from "components/atoms/Icon";
import { useFormContext } from "react-hook-form";
import BigImageUpload from "components/atoms/UploadImage";
import InputFieldWithLabel from "components/molecules/forms/InputFieldWithLabel";
import TextAreaWithLabel from "components/molecules/forms/TextAreaWithLabel";
import { styled } from "@mui/material/styles";
import MTextField from "@mui/material/TextField";
import Label from "components/atoms/forms/Label";
import { ReactNode, useEffect, useState } from "react";
import moment from "moment";
import CheckboxWithLabel from "components/molecules/forms/CheckboxWithLabel";
import HintIconWithTooltip from "components/molecules/forms/HintIconWithTooltip";
import { TOOLTIP_HINT_TEXT } from "constants/constants";
import CropperModal from "components/molecules/imagecropper/CropperModal";
import Knob from "components/atoms/forms/Knob/Knob";
import { ShieldExclamationIcon } from "@heroicons/react/20/solid";
import ErrorMessage from "components/atoms/forms/ErrorMessage";
import { isObject } from "lodash";
import MementoSpecificFields from "./MementoSpecificFields";
import RuleModal from "./RuleModal";

function MementoForm({ activationKey }: { activationKey?: ActivationsKeys }) {
  const [isRuleModalOpen, setIsRuleModapOpen] = useState(false);
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    getValues,
    trigger,
    setError,
    clearErrors,
  } = useFormContext<MementoFormType>();

  const isMemontoPublished = false;

  const mementoThumbnail = watch("thumbnail");

  const [isStartDatePickerModalOpen, setIsStartDatePickerModalOpen] = useState(false);
  const [isEndDatePickerModalOpen, setIsEndDatePickerModalOpen] = useState(false);
  const [showCropper, setShowCropper] = useState(false);

  const handleSetStartDateTimeForMomemto = (value: any) => {
    setValue("start_time", value);
  };
  const handleSetEndDateTimeForMomemto = (value: any) => {
    setValue("end_time", value);
  };

  const handleSetFieldValue = (file: FileList) => {
    if (file.item(0)?.type.startsWith("image")) {
      clearErrors("thumbnail");
      setValue("thumbnail", file.item(0) || undefined);
      if (
        file.item(0)?.type.includes("gif") ||
        file.item(0)?.type.includes("GIF") ||
        file.item(0)?.type.includes("webp") ||
        file.item(0)?.type.includes("WEBP")
      )
        trigger("thumbnail");
      else setShowCropper(true);
    } else {
      setError("thumbnail", { message: "Select valid image file (JPG, JPEG, PNG, GIF, WEBP are supported)" });
    }
  };

  const handleClearImage = () => {
    setValue("thumbnail", undefined);
  };

  useEffect(() => {
    if (errors.thumbnail?.message) {
      handleClearImage();
    }
  }, [errors.thumbnail?.message]);

  useEffect(() => {
    if (errors.media?.message || errors.media_url?.message) {
      clearErrors(["media", "media_url", "media_url_new"]);
    }
  }, [watch("media")]);

  useEffect(() => {
    if (errors.media_url?.message) {
      trigger(["media_url", "media"]);
    }
  }, [watch("is_gated")]);

  return (
    <div className="relative grid grid-cols-7 gap-x-12 gap-y-3 pr-20">
      <div className="!sticky top-0 col-span-2 row-span-4">
        {showCropper && (
          <CropperModal
            image={URL.createObjectURL(mementoThumbnail as File)}
            setImage={(croppedImage: File) => setValue("thumbnail", croppedImage, { shouldValidate: true })}
            open={showCropper}
            setOpen={setShowCropper}
            format="image/jpeg"
          />
        )}
        <BigImageUpload
          isDisabled={isMemontoPublished}
          setFieldValue={handleSetFieldValue}
          // TODO: REMOVE THIS OR STATEMENTE
          existingImageURL={getValues("thumbnail_url") || ""}
          choosenFile={mementoThumbnail}
          // @ts-ignore
          errors={errors.thumbnail?.message || errors.thumbnail_url?.message}
          labelClasses="italic text-xs text-white"
          id="thumbnail"
          handleClearImage={handleClearImage}
          icon={<ImageUploadIcon className="h-20 w-20 2xl:h-24 2xl:w-24" />}
        />
        <div className="text-center text-sm leading-6">
          <p className="font-sora font-medium text-white">Select Thumbnail Image for Your Airdrop</p>
          <p className="font-satoshi font-normal text-white">(Images and GIFs are allowed)</p> <br />
        </div>
      </div>
      <div className="col-span-3">
        <InputFieldWithLabel
          // isDisabled={isMagnetPublished}
          label="Memento Name *"
          id="name"
          register={register}
          placeHolder="Enter the name of the Memento."
          defaultValue=""
          errorMessage={errors.name?.message as any}
        />
      </div>
      <div className="col-span-2 mt-1 flex justify-between">
        <Knob
          label="Memento Type"
          id="Hello"
          isChecked={watch("is_incentive")}
          onChange={() => {
            const isIncentive = watch("is_incentive");
            if (!isIncentive) {
              setValue("is_gated", true);
            }
            setValue("is_incentive", !isIncentive);
          }}
          hintRender={
            <HintIconWithTooltip
              hintText={
                <div className="max-w-[20rem] space-y-1 py-0.5 text-xxs leading-snug">
                  <p>
                    <span className="font-medium underline">Airdrops:</span> Drop this memento to all Magnet owners at
                    the scheduled time.
                  </p>
                  <p>
                    <span className="font-medium underline">Incentives:</span> Drop this memento only to the subset of
                    Magnet owners that complete the required criterion within the specified time frame.
                  </p>
                  <p>
                    <span className="text-[0.66rem] font-medium leading-snug underline underline-offset-4">
                      {" "}
                      Note: On Creating a Content Drop Incentive the media will mandatorily be &lsquo;Exclusive&rsquo;
                    </span>
                  </p>
                </div>
              }
              direction="bottom"
            />
          }
        />
      </div>
      <div className="col-span-3 row-span-2">
        <TextAreaWithLabel
          // isDisabled={isMagnetPublished}
          label="Memento Description *"
          id="description"
          rows={5}
          placeHolder="Enter the description of the Memento."
          register={register}
          defaultValue=""
          errorMessage={errors.description?.message}
        />
      </div>
      {watch("is_incentive") ? (
        <div className="col-span-2 row-span-3 pt-1">
          <div className="flex items-start">
            <Label label="Drop Incentive When" htmlFor="start_time" />
            <HintIconWithTooltip
              hintText={
                <div className="max-w-[20rem] space-y-1 py-0.5 text-base">
                  These are the conditions that your users need to fulfil in order to grab this incentive
                </div>
              }
              direction="bottom"
            />
          </div>
          <button
            onClick={() => setIsRuleModapOpen(true)}
            type="button"
            className="mb-4 inline-flex cursor-pointer items-center gap-1 rounded-md bg-secondary bg-opacity-90 py-2.5 pl-2.5 pr-4 text-xxs font-medium text-primary shadow-sm hover:bg-opacity-100"
          >
            <ShieldExclamationIcon className="h-4 w-4" />
            {watch("incentive_rules.conditions")?.length
              ? `Edit Rules (${watch("incentive_rules.conditions").length} Rules Applied)`
              : "Add Rules"}
          </button>
          {isObject(errors.incentive_rules) && (
            <div className="-mt-2 mb-3 leading-tight">
              <ErrorMessage
                errorMessage={
                  errors.incentive_rules
                    ? errors.incentive_rules.message ||
                      "Invalid Rule: Valid rule should contain atleast one condition, if participation condition is chosen then select atleast one memento"
                    : ""
                }
              />
            </div>
          )}
          <RuleModal open={isRuleModalOpen} onClose={() => setIsRuleModapOpen(false)} />
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
          <RenderDatePicker
            label="Reward Valid Till"
            isDatePickerModalOpen={isEndDatePickerModalOpen}
            setIsDatePickerModalOpen={setIsEndDatePickerModalOpen}
            dateTime={watch("end_time")}
            error={errors.end_time?.message}
            handleSetStartDateTimeForMomemto={handleSetEndDateTimeForMomemto}
            hintRender={
              <HintIconWithTooltip
                hintText="This is the last date until when completing this incentive's requirements drops the incentives to the user. If the dropped incentive is a claimable merch or code, this also holds as the last date to claim the benefit."
                hintTextClassName="w-72 text-base leading-snug"
                direction="bottom"
                elementClassName="-mt-0.5"
              />
            }
          />
        </div>
      ) : (
        <div className="col-span-2 row-span-3 pt-1">
          <div className="">
            {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
            <RenderDatePicker
              label="Airdrop Date/Time"
              isDatePickerModalOpen={isStartDatePickerModalOpen}
              setIsDatePickerModalOpen={setIsStartDatePickerModalOpen}
              dateTime={watch("start_time")}
              error={errors.start_time?.message}
              handleSetStartDateTimeForMomemto={handleSetStartDateTimeForMomemto}
            />
          </div>
          <div className="mt-0.5 flex items-center">
            <CheckboxWithLabel
              isChecked={watch("auto_drop_on_purchase")}
              isDisabled={isMemontoPublished}
              label="Enable Airdrop for New Users"
              id="auto_drop_on_purchase"
              labelClasses="text-xxs"
              register={register}
            />
            <HintIconWithTooltip
              direction="bottom"
              hintTextClassName="w-72"
              hintText={TOOLTIP_HINT_TEXT.AutoAirdropCheckbox}
            />
          </div>
          {activationKey !== "mem_drop" && (
            <div className="mt-3">
              {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
              <RenderDatePicker
                label="Expiration Date/Time"
                isDatePickerModalOpen={isEndDatePickerModalOpen}
                setIsDatePickerModalOpen={setIsEndDatePickerModalOpen}
                dateTime={watch("end_time")}
                error={errors.end_time?.message}
                handleSetStartDateTimeForMomemto={handleSetEndDateTimeForMomemto}
              />
            </div>
          )}
        </div>
      )}
      <div className="col-span-3">
        <MementoSpecificFields activationKey={activationKey as ActivationsKeys} />
      </div>
    </div>
  );
}

export default MementoForm;

type DatePickerProps = {
  label: string;
  dateTime: number | null | moment.Moment;
  error: any;
  isDatePickerModalOpen: boolean;
  setIsDatePickerModalOpen: (s: boolean) => void;
  handleSetStartDateTimeForMomemto: (value: any) => void;
  hintRender?: ReactNode;
};

export function RenderDatePicker({
  label,
  dateTime,
  error,
  isDatePickerModalOpen,
  setIsDatePickerModalOpen,
  handleSetStartDateTimeForMomemto,
  hintRender,
}: DatePickerProps) {
  const StyledMTextField = styled(MTextField)(() => ({
    "& .MuiInputBase-input": {
      boxShadow: "none",

      color: "white",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "10px",
      borderWidth: "1px",
    },

    "& .MuiInput-underline:after": {
      borderBottomColor: "white",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "white",
        textColor: "white",
      },
      "&:hover fieldset": {
        borderColor: "white",
        textColor: "white",
      },
      "&.Mui-focused fieldset": {
        borderColor: "white",
        textColor: "white",
      },
    },
  }));

  const [anchorEl, setAnchorEl] = useState<any>(null);

  return (
    <>
      <div className="flex items-start">
        <Label label={label} htmlFor="start_time" />
        {hintRender}
      </div>

      <LocalizationProvider dateAdapter={AdapterMoment}>
        {/* Start time. */}
        <div className="w-[400px]">
          <DateTimePicker
            components={{
              OpenPickerIcon: DateIcon,
            }}
            renderInput={(props) => (
              <div onClick={(e) => setAnchorEl(e.currentTarget)}>
                <StyledMTextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...props}
                  error={error}
                  helperText={error}
                />
              </div>
            )}
            disablePast
            open={isDatePickerModalOpen}
            PopperProps={{
              placement: "bottom-start",
              anchorEl,
            }}
            onOpen={() => setIsDatePickerModalOpen(true)}
            onClose={() => setIsDatePickerModalOpen(false)}
            value={dateTime}
            minDate={new Date()}
            onChange={handleSetStartDateTimeForMomemto}
            closeOnSelect
            inputFormat="MMM DD YY hh:mm A"
            disableMaskedInput
          />
        </div>
      </LocalizationProvider>
    </>
  );
}
